/**
 * Display labels for raw API values.
 *
 * The API sends categories as database identifiers — other_item,
 * handbook_combined, adhoc_rate. They are fine as data and wrong on screen or
 * in a spreadsheet, so every place that shows one goes through humanise().
 *
 * Only values that are entirely lower-case words joined by underscores are
 * changed. Anything else — a franchise called "Learning Hub", a status already
 * written "Credit note" — is returned exactly as it came.
 */

/** Identifiers whose natural title-casing would be wrong. */
const LABEL_OVERRIDES: Record<string, string> = {
  // Spelled this way in the database.
  handbook_suppliment: 'Handbook Supplement'
};

const SNAKE_CASE = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

export const humanise = (value: any): any => {
  if (typeof value !== 'string' || !SNAKE_CASE.test(value)) return value;
  if (LABEL_OVERRIDES[value]) return LABEL_OVERRIDES[value];
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default humanise;

/**
 * "2026-08" -> "August 2026". Anything that is not a year-month is returned as
 * it came, so a bad or missing value never turns into "Invalid Date".
 */
export const monthYear = (value: any): any => {
  const match = typeof value === 'string' ? /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value) : null;
  if (!match) return value;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)).toLocaleString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

/** "2026-08" -> "Aug 2026", for chart axes where "August 2026" would not fit. */
export const monthYearShort = (value: any): any => {
  const match = typeof value === 'string' ? /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value) : null;
  if (!match) return value;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)).toLocaleString('en-GB', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });
};
